import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, CardBody, CardHeader } from "reactstrap";
import { useNavigate } from "react-router-dom";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import axiosInstance from "../../components/AxiosInstance";

const TAB_ALL = "all";
const TAB_CHILLERS = "chiller";
const TAB_COMPRESSORS = "compressor";

const MachineSelection = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [assets, setAssets] = useState({ all: [], chillers: [], compressors: [] });
  const [summary, setSummary] = useState({ total: 0, chillers: 0, compressors: 0 });
  const [activeTab, setActiveTab] = useState(TAB_ALL);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const navigate = useNavigate();

  const authToken = localStorage.getItem("access");

  const fetchAssets = async () => {
    try {
      setIsLoading(true);
      const response = await axiosInstance.get("https://api.hvacsync.in/api/assets/?type=all", {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
      });
      const { data, summary } = response.data;
      setAssets({
        all: data?.all || [],
        chillers: data?.chillers || [],
        compressors: data?.compressors || [],
      });
      setSummary(summary || { total: 0, chillers: 0, compressors: 0 });
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem("access");
      } else {
        console.error("Error fetching assets:", error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAssets();
  }, []);

  const handleAssetClick = (asset) => {
    const { id, asset_type,name } = asset;
    if (asset_type === "chiller") {
      localStorage.setItem("selectedMachineId", id);
      navigate("/chiller", { state: { id } });
    } else if (asset_type === "compressor") {
      localStorage.setItem("selectedCompressorId", id);
      navigate("/compressorData",  {state: { 
    id: id,        // ← this becomes location.state.id
    compname: name    // ← this becomes location.state.compname
  } })
    }
  };

  const displayedAssets = (() => {
    const list =
      activeTab === TAB_ALL
        ? assets.all
        : activeTab === TAB_CHILLERS
        ? assets.chillers
        : assets.compressors;

    return [...list].sort((a, b) => (b.is_running === true) - (a.is_running === true));
  })();

  const tabs = [
    { key: TAB_ALL, label: "All", count: summary.total },
    { key: TAB_CHILLERS, label: "Chillers", count: summary.chillers },
    { key: TAB_COMPRESSORS, label: "Compressors", count: summary.compressors },
  ];

  const activeCount = assets.all.filter((a) => a.is_running).length;
  const inactiveCount = summary.total - activeCount;

  document.title = "Overview | HVACSYNC";

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs title="HVACSYNC" breadcrumbItem="Overview" />

          {/* Summary Cards */}
          <Row className="mb-3">
            <Col xl={4} sm={6}>
              <Card>
                <CardBody>
                  <div className="d-flex text-muted">
                    <div className="flex-shrink-0 me-3 align-self-center">
                      <div className="avatar-sm">
                        <div
                          className="avatar-title bg-light rounded-circle text-primary"
                          style={{ fontSize: "24px", width: "40px", height: "40px", display: "flex", alignItems: "center", justifyContent: "center" }}
                        >
                          <i className="ri-time-line"></i>
                        </div>
                      </div>
                    </div>
                    <div className="flex-grow-1 overflow-hidden">
                      <p className="mb-1">Total</p>
                      <h5 className="mb-3">{summary.total}</h5>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col xl={4} sm={6}>
              <Card>
                <CardBody>
                  <div className="d-flex">
                    <div className="flex-shrink-0 me-3 align-self-center">
                      <div className="avatar-sm">
                        <div
                          className="avatar-title bg-light rounded-circle text-success"
                          style={{ fontSize: "24px", width: "40px", height: "40px", display: "flex", alignItems: "center", justifyContent: "center" }}
                        >
                          <i className="ri-checkbox-circle-line"></i>
                        </div>
                      </div>
                    </div>
                    <div className="flex-grow-1 overflow-hidden">
                      <p className="mb-1">Active</p>
                      <h5 className="mb-3">{activeCount}</h5>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col xl={4} sm={6}>
              <Card>
                <CardBody>
                  <div className="d-flex text-muted">
                    <div className="flex-shrink-0 me-3 align-self-center">
                      <div className="avatar-sm">
                        <div
                          className="avatar-title bg-light rounded-circle text-danger"
                          style={{ fontSize: "24px", width: "40px", height: "40px", display: "flex", alignItems: "center", justifyContent: "center" }}
                        >
                          <i className="ri-close-circle-line"></i>
                        </div>
                      </div>
                    </div>
                    <div className="flex-grow-1 overflow-hidden">
                      <p className="mb-1">Inactive</p>
                      <h5 className="mb-3">{inactiveCount}</h5>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Tabs */}
          <div className="mb-3">
            <ul className="nav nav-tabs nav-tabs-custom justify-content-center" role="tablist">
              {tabs.map((tab) => (
                <li className="nav-item" key={tab.key}>
                  <button
                    className={`nav-link${activeTab === tab.key ? " active" : ""}`}
                    onClick={() => setActiveTab(tab.key)}
                    type="button"
                    role="tab"
                    style={{ cursor: "pointer" }}
                  >
                    {tab.label}
                    <span
                      className={`badge ms-2 ${
                        activeTab === tab.key ? "bg-primary" : "bg-secondary"
                      }`}
                    >
                      {tab.count}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Asset Cards */}
          {isLoading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <Row>
              {displayedAssets.length === 0 ? (
                <Col>
                  <p className="text-muted text-center py-4">No assets found.</p>
                </Col>
              ) : (
                displayedAssets.map((asset, index) => (
                  <Col xl={3} sm={6} key={`${asset.asset_type}-${asset.id}-${index}`} className="mb-4 d-flex">
                    <Card
                      onClick={() => handleAssetClick(asset)}
                      onMouseEnter={() => setHoveredIndex(index)}
                      onMouseLeave={() => setHoveredIndex(null)}
                      className="w-100"
                      style={{
                        cursor: "pointer",
                        display: "flex",
                        flexDirection: "column",
                        transform: hoveredIndex === index ? "translateY(-3px)" : "none",
                        boxShadow:
                          hoveredIndex === index
                            ? "0 4px 12px rgba(0, 0, 0, 0.15)"
                            : "none",
                        // border:
                        //   hoveredIndex === index
                        //     ? "1px solid rgb(20, 33, 60)"
                        //     : "1px solid transparent",
                        transition: "transform 0.2s ease, box-shadow 0.2s ease, border 0.2s ease",
                      }}
                    >
                      <CardHeader>
                        <div className="d-flex justify-content-between align-items-center">
                          <span>{asset?.name}</span>
                          <div className="d-flex align-items-center gap-2">
                            <span
                              className={`badge ${
                                asset.asset_type === "chiller"
                                  ? "bg-info"
                                  : "bg-warning text-dark"
                              }`}
                              style={{ fontSize: "10px" }}
                            >
                              {asset.asset_type === "chiller" ? "Chiller" : "Compressor"}
                            </span>
                            <img
                              width="12"
                              height="12"
                              src={
                                asset?.is_running
                                  ? "https://img.icons8.com/ios-filled/50/40C057/filled-circle.png"
                                  : "https://img.icons8.com/ios-filled/50/FA5252/filled-circle.png"
                              }
                              alt={asset?.is_running ? "Active" : "Inactive"}
                            />
                          </div>
                        </div>
                      </CardHeader>
                      <CardBody
                        style={{
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <img
                          src={`https://api.hvacsync.in/media/${asset.image}`}
                          alt={asset.name}
                          height="50"
                          className="img-fluid"
                        />
                        {/* Always render this block to keep height consistent */}
                        <div className="text-center mt-2" style={{ minHeight: "20px" }}>
                          {asset.asset_type === "chiller" && Array.isArray(asset.compressors) && (
                            <small className="text-muted">
                              <i className="ri-tools-line me-1"></i>
                              {asset.compressors.length} Compressor{asset.compressors.length !== 1 ? "s" : ""}
                            </small>
                          )}
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                ))
              )}
            </Row>
          )}
        </Container>
      </div>
    </React.Fragment>
  );
};

export default MachineSelection;